import { FC, useEffect, useMemo, useState } from "react";
import { Box, useTheme } from "@mui/material";
import { FormikHelpers } from "formik";
import { Table } from "@vilocnv/allsetra-core";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import TagFilterForm from "components/forms/Reports/TagFilterForm";
import TagPositionDateRowCard from "components/cards/ReportCards/TagPositionDateRowCard/TagPositionDateRowCard";
import ReportsTopbar from "components/common/reports/ReportsTopbar/ReportsTopbar";

// Data
import { useAppDispatch, useAppSelector, useReportCommonStates } from "hooks";
import {
  generateReportThunk,
  resetReportsFilterData,
  setReportsFilterData,
  setReportsDateValues
} from "app/features";
import {
  convertToNormalTime,
  formatTimeWithDay,
  organizeData,
  reportTransformTimeForAPI,
  getStandardizedReportDates,
  reportsTagFilterFormInitialValues,
  reportsTagFilterFormValidationSchema
} from "app/data/helpers";
import {
  buildTagPositionObjectLookup,
  getResetFilterFormValues,
  getTagPositionLocationLabel
} from "app/data/helpers/ReportHelpers";
import {
  TAG_POSITION_HEADERS,
  TAG_POSITION_TABLE_COLUMNS
} from "app/data/constants";
import { selectReportsState } from "app/data/selectors";
import { useTranslation } from "react-i18next";
import ReportsFilterBar from "components/common/reports/ReportFiltersBar/ReportsFilterBar";

const TagPosition: FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const {
    objectTypes,
    objectTypesLoading,
    objectsLoading,
    accountGroups,
    report,
    reportsLoading,
    filterOpen,
    setFilterOpen,
    dateFormat,
    setDateFormat,
    dateValues,
    setDateValues,
    localReport,
    setLocalReport,
    handleObjectsSearch,
    handleOnChangeReportFilterForm,
    reportsFilterData,
    filterValues,
    setFilterValues,
    mergedObjectOptions,
    basicObjects,
    getHydratedFormInitialValues,
    setSelectedLocalIds,
    resetReportDates
  } = useReportCommonStates();

  const { resolvedAddresses } = useAppSelector(selectReportsState);

  const { t } = useTranslation(["translation", "tableHeadingsTranslation"]);
  const [apiPayload, setApiPayload] = useState(() =>
    reportsFilterData && Object.keys(reportsFilterData).length
      ? reportsFilterData
      : null
  );

  const tableColumns = useMemo(() => TAG_POSITION_TABLE_COLUMNS(t), [t]);

  const objectLookup = useMemo(
    () =>
      buildTagPositionObjectLookup(
        basicObjects?.length ? basicObjects : mergedObjectOptions
      ),
    [basicObjects, mergedObjectOptions]
  );

  const formattedData = useMemo(
    () => organizeData(localReport, dateFormat, objectLookup),
    [localReport, dateFormat, objectLookup]
  );

  const tagPositionFiltersSubmitHandler = async (
    values: any,
    formikHelpers: FormikHelpers<any>
  ) => {
    formikHelpers.setSubmitting(true);

    const startDate = new Date(dateValues.startDate);
    const endDate = new Date(dateValues.endDate);

    const sameDate = startDate.toDateString() === endDate.toDateString();

    if (sameDate) {
      startDate.setHours(0, 0, 0);
      endDate.setHours(23, 59, 59);
    }

    const formattedStartDate = convertToNormalTime(startDate);
    const formattedEndDate = convertToNormalTime(endDate, true);
    setFilterValues(values);

    const startTime = values.startTime ?? "00:00";
    const endTime = values.endTime ?? "23:59";

    const payload = {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      ...values,
      object: values.assignedToAllObjects ? [] : values.object,
      reportType: 1,
      daysOfWeek:
        values.daysOfWeek.length === 7 ? [] : values.daysOfWeek.map(Number),
      startTime: reportTransformTimeForAPI(`${startTime}:00`, startDate, false),
      endTime: reportTransformTimeForAPI(
        `${endTime}:00`,
        endDate,
        true,
        startDate
      )
    };

    dispatch(setReportsFilterData(payload));
    setApiPayload(payload);
    await dispatch(generateReportThunk(payload));

    formikHelpers.setSubmitting(false);
    setFilterOpen(false);
  };

  const tagPositionCSVData = () =>
    formattedData.flatMap((objectData) =>
      objectData.details.flatMap((dayData) =>
        dayData.records.map((record) => ({
          ObjectName: objectData.name,
          ID: objectData.aNumber || "-",
          Time: formatTimeWithDay(record.date),
          Location: getTagPositionLocationLabel(record, resolvedAddresses, "-"),
          Accuracy: `${record?.accuracy ?? ""}m`,
          Accurate: record?.isAccurate?.toString() ?? "",
          LatLong: `${record?.latitude ?? 0} ${record?.longitude ?? 0}`
        }))
      )
    );

  const generateTagPositionPDF = () => {
    const columns = [
      "Object Name",
      "ID",
      "Time",
      "Location",
      "Accuracy",
      "Accurate",
      "Lat Long"
    ];
    const rows = formattedData.flatMap((objectData) =>
      objectData.details.flatMap((dayData) =>
        dayData.records.map((record) => [
          objectData.name,
          objectData.aNumber || "-",
          formatTimeWithDay(record.date),
          getTagPositionLocationLabel(record, resolvedAddresses, "-"),
          `${record.accuracy}m`,
          record.isAccurate.toString(),
          `${record.latitude} ${record.longitude}`
        ])
      )
    );
    const doc = new jsPDF();
    doc.text("Tag Position Report", 10, 10);
    autoTable(doc, {
      head: [columns],
      body: rows
    });
    doc.save("Tag Position Report.pdf");
  };

  const handleDateChange = (value: any) => {
    const formattedValue = getStandardizedReportDates(value);
    setDateValues(formattedValue);
    dispatch(setReportsDateValues(formattedValue));
  };

  const buildTagPositionPayload = () => {
    const startDate = convertToNormalTime(dateValues.startDate);
    const endDate = convertToNormalTime(dateValues.endDate, true);

    let updatedFilterValues = { ...filterValues };
    if (filterValues.daysOfWeek) {
      const startTime = filterValues.startTime ?? "00:00";
      const endTime = filterValues.endTime ?? "23:59";

      updatedFilterValues = {
        ...filterValues,
        daysOfWeek:
          filterValues.daysOfWeek.length === 7
            ? []
            : filterValues.daysOfWeek.map(Number),
        startTime: reportTransformTimeForAPI(
          `${startTime}:00`,
          dateValues.startDate,
          false
        ),
        endTime: reportTransformTimeForAPI(
          `${endTime}:00`,
          dateValues.endDate,
          true,
          new Date(dateValues.startDate)
        )
      };
    }

    return {
      startDate,
      endDate,
      ...updatedFilterValues,
      object: filterValues.assignedToAllObjects
        ? []
        : (filterValues.object ?? []),
      reportType: 1
    };
  };

  const handleDateRangeSubmit = () => {
    setApiPayload(buildTagPositionPayload());
  };

  const handleLoadReport = () => {
    const payload = apiPayload ?? buildTagPositionPayload();

    if (!payload?.startDate || !payload?.endDate) {
      return;
    }

    dispatch(setReportsFilterData(payload));
    dispatch(generateReportThunk(payload));
  };

  useEffect(() => {
    handleDateRangeSubmit();
  }, []);

  useEffect(() => {
    setLocalReport(report);
  }, [report]);

  return (
    <Box>
      <ReportsTopbar dropdownTitle={t("reports.tagPosition")} />
      <Box mx={4}>
        <ReportsFilterBar
          setFilterOpen={setFilterOpen}
          dateFormat={dateFormat}
          setDateFormat={setDateFormat}
          handleDateChange={handleDateChange}
          dateValues={dateValues}
          handleDateRangeSubmit={handleDateRangeSubmit}
          disableExportButton={!formattedData?.length}
          dateRangeLoading={reportsLoading}
          exportHeaders={TAG_POSITION_HEADERS}
          generateCSVData={tagPositionCSVData}
          formattedData={formattedData}
          generatePDF={generateTagPositionPDF}
          hideScheduleButton={true}
          handleLoadReport={handleLoadReport}
        />
        <TagFilterForm
          fetchOnDebounce={handleObjectsSearch}
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          onSubmit={tagPositionFiltersSubmitHandler}
          groups={accountGroups}
          objectTypes={objectTypes}
          objects={mergedObjectOptions}
          dataLoading={objectsLoading || objectTypesLoading}
          theme={theme}
          //@ts-ignore
          onChange={handleOnChangeReportFilterForm}
          formInitialValues={getHydratedFormInitialValues(
            reportsTagFilterFormInitialValues
          )}
          formValidations={reportsTagFilterFormValidationSchema}
          onResetFilters={() => {
            dispatch(resetReportsFilterData());
            resetReportDates();
            setSelectedLocalIds([]);
            setApiPayload(null);
            setFilterValues(
              getResetFilterFormValues(reportsTagFilterFormInitialValues)
            );
          }}
        />
        <Table
          columns={tableColumns}
          data={formattedData}
          progressPending={reportsLoading}
          expandableRows
          expandOnRowClicked
          expandableRowsComponent={TagPositionDateRowCard}
          pagination={false}
          translator={t}
        />
      </Box>
    </Box>
  );
};

export default TagPosition;
